import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Rating,
  Chip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Close as CloseIcon,
  Share as ShareIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { GalleryItem } from './types';
import { getCategoryGradient, handleCall, handleWhatsApp, handleShare } from './utils';

interface ProductModalProps {
  product: GalleryItem | null;
  open: boolean;
  onClose: () => void;
}

const ProductModal: React.FC<ProductModalProps> = ({
  product,
  open,
  onClose
}) => {
  if (!product) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={window.innerWidth < 768}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, md: '16px' },
          maxHeight: { xs: '100vh', md: '90vh' },
          margin: { xs: 0, md: '32px' },
        }
      }}
    >
      <DialogTitle sx={{ 
        position: 'relative',
        background: getCategoryGradient(product.category),
        color: 'white',
        pb: 2,
        px: { xs: 2, md: 3 },
        pt: { xs: 2, md: 3 }
      }}>
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography 
              variant="h5" 
              component="h2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}
            >
              {product.title}
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Rating value={product.rating} readOnly precision={0.1} size="small" />
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              ({product.reviews} reviews)
            </Typography>
            <Chip 
              label={product.availability}
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Box>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 1
        }} />
      </DialogTitle>

      <DialogContent 
        dividers 
        sx={{ 
          p: 0,
          overflowY: 'auto',
          maxHeight: { xs: 'calc(100vh - 200px)', md: 'none' }
        }}
      >
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
            gap: { xs: 2, md: 3 }
          }}>
            <Box>
              <Box sx={{
                height: { xs: 200, md: 300 },
                background: getCategoryGradient(product.category),
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                position: 'relative',
                mb: 2
              }}>
                <Typography 
                  variant="h4" 
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}
                >
                  {product.category}
                </Typography>
              </Box>
              
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', mb: 1 }}>
                Key Features
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {product.features.map((feature, index) => (
                  <Chip
                    key={index}
                    label={feature}
                    size="small"
                    sx={{
                      backgroundColor: '#e8eaf6',
                      color: '#1a237e',
                      fontWeight: '500'
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', mb: 2 }}>
                Product Description
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                {product.description}
              </Typography>

              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', mb: 1 }}>
                Specifications
              </Typography>
              <List dense>
                {product.specifications.map((spec, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemText 
                      primary={spec}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { color: '#666' }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: { xs: 2, md: 3 }, 
        gap: { xs: 1, md: 2 },
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        <Button
          variant="outlined"
          onClick={() => handleShare(product)}
          startIcon={<ShareIcon />}
          fullWidth={window.innerWidth < 768}
          sx={{ 
            borderColor: '#1a237e', 
            color: '#1a237e',
            minHeight: { xs: '48px', md: '36px' },
            fontSize: { xs: '0.9rem', md: '0.875rem' },
            flex: { sm: '1 1 auto' },
            maxWidth: { sm: '150px', md: 'none' }
          }}
        >
          Share
        </Button>
        <Button
          variant="contained"
          onClick={() => handleWhatsApp(product.title)}
          startIcon={<WhatsAppIcon />}
          fullWidth={window.innerWidth < 768}
          sx={{
            backgroundColor: '#25d366',
            minHeight: { xs: '48px', md: '36px' },
            fontSize: { xs: '0.9rem', md: '0.875rem' },
            flex: { sm: '1 1 auto' },
            maxWidth: { sm: '180px', md: 'none' },
            '&:hover': { backgroundColor: '#128c7e' }
          }}
        >
          WhatsApp Inquiry
        </Button>
        <Button
          variant="contained"
          onClick={handleCall}
          startIcon={<PhoneIcon />}
          fullWidth={window.innerWidth < 768}
          sx={{
            backgroundColor: '#1a237e',
            minHeight: { xs: '48px', md: '36px' },
            fontSize: { xs: '0.9rem', md: '0.875rem' },
            flex: { sm: '1 1 auto' },
            maxWidth: { sm: '150px', md: 'none' },
            '&:hover': { backgroundColor: '#3949ab' }
          }}
        >
          Call Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductModal;
